import { useEffect, useState } from "react";
import { format, startOfMonth, subDays } from "date-fns";
import { CalendarDays, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";

const toDate = (s) => { if (!s) return undefined; const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const toStr = (d) => (d ? format(d, "yyyy-MM-dd") : "");
const label = (s, e) => {
  if (!s && !e) return "Any date";
  if (s && e && s.getTime() === e.getTime()) return format(s, "d MMM yyyy");
  if (s && e) return `${format(s, s.getFullYear() === e.getFullYear() ? "d MMM" : "d MMM yyyy")} – ${format(e, "d MMM yyyy")}`;
  return s ? `From ${format(s, "d MMM yyyy")}` : `Until ${format(e, "d MMM yyyy")}`;
};

const useWide = () => {
  const [wide, setWide] = useState(() => window.innerWidth >= 768);
  useEffect(() => {
    const on = () => setWide(window.innerWidth >= 768);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return wide;
};

const GOLD = {
  caption_label: "font-serif text-sm font-semibold tracking-wide text-white",
  nav_button: "inline-grid h-8 w-8 place-items-center rounded-full border border-[#D4AF37]/40 text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/15",
  head_cell: "w-9 text-[10px] font-semibold uppercase tracking-wider text-[#D4AF37]/70",
  cell: "relative h-9 w-9 p-0 text-center text-sm [&:has(.day-range-middle)]:bg-[#D4AF37]/15 [&:has(.day-range-start)]:rounded-l-full [&:has(.day-range-end)]:rounded-r-full [&:has(.day-range-start)]:bg-[#D4AF37]/15 [&:has(.day-range-end)]:bg-[#D4AF37]/15",
  day: "h-9 w-9 rounded-full p-0 text-sm font-normal text-zinc-200 transition-colors hover:bg-[#D4AF37]/20 hover:text-white focus:outline-none focus-visible:ring-1 focus-visible:ring-[#D4AF37]",
  day_selected: "!bg-gradient-to-b !from-[#F3D77A] !to-[#D4AF37] !font-bold !text-black",
  day_range_middle: "day-range-middle !bg-transparent !from-transparent !to-transparent !font-normal !text-[#F3D77A]",
  day_today: "ring-1 ring-inset ring-[#D4AF37]/70 text-[#F3D77A]",
  day_outside: "day-outside text-zinc-600 opacity-60",
  day_disabled: "text-zinc-700 opacity-40 hover:bg-transparent",
};

const PRESETS = [
  ["Last 7 days", () => [subDays(new Date(), 6), new Date()]],
  ["Last 30 days", () => [subDays(new Date(), 29), new Date()]],
  ["This month", () => [startOfMonth(new Date()), new Date()]],
];

export const GoldDatePicker = ({ start, end, onChange, testid = "gold-date" }) => {
  const [open, setOpen] = useState(false);
  const wide = useWide();
  const from = toDate(start);
  const to = toDate(end);
  const active = !!(start || end);
  const pick = (r) => {
    onChange({ start: toStr(r?.from), end: toStr(r?.to || r?.from) });
    if (r?.from && r?.to && r.from.getTime() !== r.to.getTime()) setOpen(false);
  };

  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={`${testid}-trigger`}
            className={`inline-flex h-11 items-center gap-2.5 rounded-full border px-5 text-sm transition-colors ${active ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F3D77A]" : "border-[#D4AF37]/30 bg-black/40 text-zinc-300 hover:border-[#D4AF37]/70"}`}>
            <CalendarDays size={16} className="text-[#D4AF37]" />
            <span data-testid={`${testid}-label`}>{label(from, to)}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={8} data-testid={`${testid}-popover`}
          className="w-auto rounded-2xl border border-[#D4AF37]/40 bg-[#0F1015] p-0 font-sans text-white shadow-[0_20px_60px_-15px_rgba(212,175,55,0.35)]">
          <div className="flex flex-col md:flex-row">
            <div className="flex flex-wrap gap-2 border-b border-white/5 p-3 md:w-40 md:flex-col md:border-b-0 md:border-r">
              {PRESETS.map(([l, fn]) => (
                <button key={l} type="button" onClick={() => { const [s, e] = fn(); onChange({ start: toStr(s), end: toStr(e) }); setOpen(false); }}
                  data-testid={`${testid}-preset-${l.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-full border border-[#D4AF37]/25 px-3 py-1.5 text-left text-xs text-zinc-300 transition-colors hover:border-[#D4AF37] hover:text-[#F3D77A]">{l}</button>
              ))}
            </div>
            <Calendar mode="range" selected={{ from, to }} onSelect={pick} numberOfMonths={wide ? 2 : 1}
              defaultMonth={from || subDays(new Date(), wide ? 30 : 0)} disabled={{ after: new Date() }} classNames={GOLD} className="p-4" />
          </div>
          <div className="flex items-center justify-between border-t border-white/5 px-4 py-3 text-xs">
            <span className="text-zinc-500">{from && !to ? "Pick an end date" : "Pick a start and end date"}</span>
            <div className="flex gap-3">
              {active && <button type="button" onClick={() => { onChange({ start: "", end: "" }); setOpen(false); }} className="uppercase tracking-widest text-zinc-400 hover:text-white" data-testid={`${testid}-reset`}>Clear</button>}
              <button type="button" onClick={() => setOpen(false)} className="font-semibold uppercase tracking-widest text-[#D4AF37]" data-testid={`${testid}-done`}>Done</button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {active && (
        <button type="button" onClick={() => onChange({ start: "", end: "" })} aria-label="Clear dates" data-testid={`${testid}-clear`}
          className="inline-grid h-9 w-9 place-items-center rounded-full border border-white/10 text-zinc-400 transition-colors hover:border-[#D4AF37] hover:text-[#F3D77A]"><X size={15} /></button>
      )}
    </div>
  );
};
